"use client";

import { Fragment, useId, type ReactNode } from "react";

import { FUOCO_HOME } from "@/components/home/stile";

/**
 * I pezzi della ricerca delle pagine in stile «prima pagina», scritti una
 * volta sola. Oggi li vestono la scheda paese (<RicercaRullo>), il
 * pannello del telefono di <BarraRicerca> (<PannelloRicerca>) e, per il
 * contorno e la freccia, <FilterSelect>.
 *
 * /rullo, /ricerca e /editoriali non passano più di qui: dal 02/10/2026
 * hanno la veste del riferimento approvato, con le classi di
 * components/rullo/registro/stile.ts (`CAMPO_REGISTRO`, `MENU_REGISTRO`,
 * `CERCA_REGISTRO`, `FILTRO_REGISTRO`). Le due vesti differiscono proprio
 * in tre delle quattro regole qui sotto: il riferimento non accende il
 * menu scelto, non scrive i conteggi e non ha la riga «Filtri attivi»
 * (le etichette con la × stanno fuori dal pannello, senza titolo). Chi
 * porta la scheda paese al registro può portarci anche questo file, e
 * allora resterebbe solo per le pagine in monospazio.
 *
 * È la stessa famiglia di <BarraRicerca>, che resta la barra delle
 * pagine in monospazio: stessa grammatica, pelle diversa. Le quattro
 * regole che le due famiglie condividono, e che prima ognuna delle tre
 * barre risolveva a modo suo:
 *
 * - **etichette**: ogni controllo ha un nome visibile, sempre lo stesso
 *   per lo stesso filtro su tutto il sito (`ETICHETTE`). Sugli editoriali
 *   c'era solo un `aria-label` e il menu si presentava come «Tutte le
 *   categorie», cioè col suo valore e non col suo nome;
 * - **stato attivo**: un filtro acceso si vede da chiuso. Nella barra in
 *   monospazio il menu si riempie d'accento; qui il controllo prende il
 *   bordo accento e il filetto a sinistra (`controlloFaccetta`). Prima
 *   un menu con «Serbia» scelto era identico a uno con «Tutti i Paesi»;
 * - **reset**: ogni filtro acceso diventa un'etichetta con la sua ✕, il
 *   testo cercato ha un comando suo e «Azzera filtri» toglie tutti i
 *   restringimenti ma non il testo (`FiltriAttivi`);
 * - **conteggio**: accanto a una voce, quanti risultati porta, scritto
 *   come nella barra in monospazio: «Energia · 269» (`conConteggio`).
 *   Il rullo scriveva «Energia (269)», gli editoriali niente.
 *
 * Le faccette si dividono in **geografiche** (dove) e **tematiche** (di
 * che cosa), più il tempo: `GruppoFaccette` le tiene in tre gruppi
 * separati da un filetto, con la loro legenda.
 */

/** I nomi dei filtri. «Paese o area» e non «Paese»: il menu porta anche
 *  le aree che a monte stanno fra i paesi («Africa Settentrionale»,
 *  «Balcani») e, sulle pagine che lo hanno, le rubriche geografiche
 *  («Maghreb», «Medio Oriente»). Chiamarlo «Paese» faceva cercare quelle
 *  voci altrove. */
export const ETICHETTE = {
  luogo: "Paese o area",
  categoria: "Categoria",
  periodo: "Periodo",
  tipo: "Tipo di dispaccio",
} as const;

/** L'etichetta mono sopra ogni controllo. */
export const ETICHETTA_FACCETTA =
  "mb-1.5 block font-mono text-nota font-semibold uppercase tracking-etichetta text-muted";

/** Il contorno di un controllo, spento o acceso.
 *
 *  Acceso: bordo accento, filetto accento di tre pixel all'interno a
 *  sinistra e testo in neretto. Il filetto è un'ombra interna e non un
 *  bordo più spesso, così il controllo non cambia misura accendendosi e
 *  la riga non si muove. Il colore da solo non basterebbe (chi non
 *  distingue l'accento dal grigio del bordo vede comunque il filetto e
 *  il neretto). */
export function controlloFaccetta(attivo: boolean): string {
  return `min-h-[44px] w-full min-w-0 cursor-pointer border bg-bg font-sans text-voce text-ink transition-colors ${
    attivo
      ? "border-accent font-semibold shadow-[inset_3px_0_0_var(--color-accent)]"
      : "border-rule hover:border-sommesso"
  }`;
}

/** «Energia · 269». Senza numero (o con zero, cioè una voce arrivata
 *  dall'indirizzo che la lista non conta) resta il solo nome. */
export function conConteggio(nome: string, n: number | undefined): string {
  return n ? `${nome} · ${n.toLocaleString("it-IT")}` : nome;
}

export function IconaX({ className }: { className: string }) {
  return (
    <svg aria-hidden viewBox="0 0 16 16" className={className} fill="none" stroke="currentColor" strokeWidth="1.7">
      <path d="M3.5 3.5l9 9M12.5 3.5l-9 9" />
    </svg>
  );
}

export function IconaGiu({ aperto, className = "" }: { aperto: boolean; className?: string }) {
  return (
    <svg
      aria-hidden
      viewBox="0 0 12 12"
      className={`h-3 w-3 shrink-0 text-muted transition-transform ${aperto ? "rotate-180" : ""} ${className}`}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
    >
      <path d="M2 4.5l4 4 4-4" />
    </svg>
  );
}

export type VoceScelta = { valore: string; testo: string };
export type GruppoScelta = { etichetta?: string; voci: VoceScelta[] };

/** Un filtro a scelta singola: un `<select>` nativo con la sua etichetta.
 *  Nativo di proposito: sul telefono apre il selettore del sistema, e la
 *  lista dei paesi è di un centinaio di voci.
 *
 *  Le voci arrivano in gruppi: un gruppo con etichetta diventa un
 *  `<optgroup>`, uno senza resta una fila di opzioni nude. È così che il
 *  menu geografico tiene separati paesi, aree e rubriche. */
export function Scelta({
  etichetta,
  valore,
  vuoto,
  gruppi,
  onCambia,
  etichettaNascosta = false,
  name,
}: {
  etichetta: string;
  valore: string;
  vuoto: string;
  gruppi: GruppoScelta[];
  onCambia: (v: string) => void;
  /** Il nome resta per chi non vede, ma non occupa la riga: serve dove
   *  un titolo accanto dice già che cosa si sta filtrando. */
  etichettaNascosta?: boolean;
  /** Il nome del campo, dove il `<select>` sta dentro un modulo GET che
   *  deve funzionare anche senza JavaScript. */
  name?: string;
}) {
  const id = useId();
  /* Un valore che arriva dall'indirizzo ma non è nel menu (un paese
     scritto a mano, una categoria senza dispacci in questa lista) resta
     selezionabile: altrimenti il menu direbbe «Tutti» mentre la lista è
     filtrata. */
  const presente = gruppi.some((g) => g.voci.some((v) => v.valore === valore));
  const tutti: GruppoScelta[] =
    valore && !presente ? [{ voci: [{ valore, testo: valore }] }, ...gruppi] : gruppi;
  const opzioni = (voci: VoceScelta[]) =>
    voci.map((v) => (
      <option key={v.valore} value={v.valore}>
        {v.testo}
      </option>
    ));
  return (
    <div className="min-w-0">
      <label htmlFor={id} className={etichettaNascosta ? "sr-only" : ETICHETTA_FACCETTA}>
        {etichetta}
      </label>
      <div className="relative">
        <select
          id={id}
          name={name}
          value={valore}
          onChange={(e) => onCambia(e.target.value)}
          className={`${controlloFaccetta(Boolean(valore))} block appearance-none truncate py-2 pr-9 pl-3 ${FUOCO_HOME}`}
        >
          <option value="">{vuoto}</option>
          {tutti.map((g, i) =>
            g.voci.length === 0 ? null : g.etichetta ? (
              <optgroup key={g.etichetta} label={g.etichetta}>
                {opzioni(g.voci)}
              </optgroup>
            ) : (
              <Fragment key={`nude-${i}`}>{opzioni(g.voci)}</Fragment>
            )
          )}
        </select>
        <IconaGiu aperto={false} className="pointer-events-none absolute top-1/2 right-3 -translate-y-1/2" />
      </div>
    </div>
  );
}

/** Un gruppo di faccette: dove, di che cosa, quando. Un `<fieldset>` con
 *  la sua legenda, perché chi arriva con un lettore di schermo sente
 *  «Dove, Paese o area» e non una fila di menu senza ordine. */
export function GruppoFaccette({
  legenda,
  children,
  className = "",
}: {
  legenda: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <fieldset className={`m-0 min-w-0 border-0 p-0 ${className}`}>
      <legend className="mb-2.5 p-0 font-mono text-nota font-semibold uppercase tracking-etichetta text-accent">
        {legenda}
      </legend>
      {children}
    </fieldset>
  );
}

export type FiltroAttivo<T> = {
  chiave: string;
  /** Il nome per chi non vede: «Togli il filtro paese Serbia». */
  nome: string;
  etichetta: ReactNode;
  /** Che cosa toglie: lo interpreta chi rende, con `onTogli`. Dati e non
   *  una funzione, così la lista si compone nel render senza chiusure
   *  (vedi il commento in <RicercaRullo>). */
  togli: T;
};

/** La riga dei filtri attivi: un'etichetta con la ✕ per ognuno, poi il
 *  comando del testo cercato e «Azzera filtri». Non si rende se non c'è
 *  niente da togliere. */
export function FiltriAttivi<T>({
  filtri,
  onTogli,
  testo,
  onCancellaTesto,
  onAzzera,
  className = "",
}: {
  filtri: FiltroAttivo<T>[];
  onTogli: (f: FiltroAttivo<T>) => void;
  /** Il testo cercato: ha un comando suo, distinto dalle etichette.
   *  Non è un filtro, e «Azzera filtri» non lo tocca. */
  testo?: string;
  onCancellaTesto?: () => void;
  onAzzera?: () => void;
  className?: string;
}) {
  if (!filtri.length && !testo) return null;
  return (
    <div className={`flex flex-wrap items-center gap-x-3 gap-y-2 ${className}`}>
      {filtri.length ? (
        <>
          <span className="font-mono text-nota font-semibold uppercase tracking-etichetta text-muted">
            Filtri attivi
          </span>
          <ul className="m-0 flex list-none flex-wrap gap-2 p-0">
            {filtri.map((f) => (
              <li key={f.chiave}>
                <button
                  type="button"
                  onClick={() => onTogli(f)}
                  aria-label={`Togli il filtro ${f.nome}`}
                  className={`inline-flex min-h-[34px] cursor-pointer items-center gap-2 border border-accent bg-bg pr-2.5 pl-3 font-sans text-voce text-ink shadow-[inset_3px_0_0_var(--color-accent)] transition-colors hover:border-ink ${FUOCO_HOME}`}
                >
                  {f.etichetta}
                  <IconaX className="h-3 w-3 text-muted" />
                </button>
              </li>
            ))}
          </ul>
        </>
      ) : null}
      <div className="ml-auto flex flex-wrap items-center gap-x-4 gap-y-1">
        {testo && onCancellaTesto ? (
          <button
            type="button"
            onClick={onCancellaTesto}
            className={`inline-flex min-h-[34px] cursor-pointer items-center gap-1.5 border-0 bg-transparent px-1 font-sans text-voce text-muted hover:text-ink ${FUOCO_HOME}`}
          >
            <IconaX className="h-3 w-3" />
            Cancella la ricerca «{testo}»
          </button>
        ) : null}
        {filtri.length && onAzzera ? (
          <button
            type="button"
            onClick={onAzzera}
            className={`inline-flex min-h-[34px] cursor-pointer items-center border-0 bg-transparent px-1 font-sans text-voce font-semibold text-accent underline underline-offset-4 hover:text-ink ${FUOCO_HOME}`}
          >
            Azzera filtri
          </button>
        ) : null}
      </div>
    </div>
  );
}
